import {
  ApolloError,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import classNames from 'classnames';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { withNotification } from 'components/Notification';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { Toggle } from 'components/Toggle';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import { UPDATE_RETURN_SHIPPING_OPTIONS_SETTINGS } from 'gql/mutations';
import {
  EASYSHIP_RETURN_CARRIER_SERVICES,
  GET_RETURN_SHIPPING_OPTIONS_SETTINGS,
  SHOPIFY_RETURN_LOCATIONS,
} from 'gql/queries';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSettingsSaveState } from 'hooks/useSettingsSaveState';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MenuAlt2Icon,
  ShieldCheckIcon,
  PlusIcon,
  RefreshIcon,
  TrashIcon,
  PencilIcon,
  TruckIcon,
} from '@heroicons/react/outline';
import { CheckIcon, SearchIcon } from '@heroicons/react/solid';
import { Combobox, Transition } from '@headlessui/react';
import { ReturnLocation } from 'types/policy';
import {
  getCarrierOptions,
  getRecommendedServiceSelection,
  getServiceOptionsForCarrier,
  SearchablePickerOption,
} from './CarrierSettings.servicePicker';
import {
  getShippingSettingsValidationError,
  getOrderedShippingOptions,
  getReorderedShippingOptions,
  isShippingOptionAvailable,
} from './CarrierSettings.shippingOptions';
import {
  RETURN_LOCATION_FIELD_LIMITS,
  ReturnLocationValidationErrors,
  getReturnLocationValidationErrors,
  normalizeReturnLocationForSave,
  sanitizeCountryCodeInput,
} from './CarrierSettings.returnLocations';
import ReturnsSettingsTabs from './ReturnsSettingsTabs';
import SharedResolutionSettingsNotice from './SharedResolutionSettingsNotice';
import ReturnLabelSettings from './ReturnLabelSettings';
import {
  ShippingCostConfig,
  ShippingCostResponsibility,
} from 'types/incentive';
import { sanitizePhoneInput } from 'utils/settingsInput';

type ReturnShippingOptionSource = 'SHIPAID_RATES' | 'MERCHANT_CARRIER_ACCOUNT';

type ReturnShippingOptionSetting = {
  id: string;
  source: ReturnShippingOptionSource;
  courierServiceId: string;
  carrier: string;
  service: string;
  displayName: string;
  enabled: boolean;
  customerVisible: boolean;
  sortOrder: number;
  meta?: Record<string, unknown> | null;
};

type ReturnCarrierService = {
  source: ReturnShippingOptionSource;
  courierServiceId: string;
  carrier: string;
  service: string;
  displayName?: string;
  active?: boolean | null;
  domestic?: boolean | null;
  international?: boolean | null;
  meta?: Record<string, unknown> | null;
};

const hasSavedReturnAddress = (returnAddress: any) =>
  Boolean(
    returnAddress &&
      [
        returnAddress.street,
        returnAddress.city,
        returnAddress.state,
        returnAddress.zip,
      ].filter(Boolean).length > 0,
  );

const normalizeShopifyReturnLocations = (
  locations: any[] = [],
): ReturnLocation[] =>
  locations.map((location, index) =>
    normalizeReturnLocationForSave(
      {
        ...location,
        street: Object.prototype.hasOwnProperty.call(location ?? {}, 'street1')
          ? location?.street1 ?? ''
          : location?.street ?? '',
        id: location?.id || location?.shopifyLocationId || `shopify-${index}`,
        label: location?.label || 'Shopify warehouse',
        isDefault: location?.isDefault ?? index === 0,
        source: 'SHOPIFY',
        shopifyLocationId: location?.shopifyLocationId || location?.id,
      },
      `shopify-${index}`,
    ),
  );

const parseOptionalNumber = (value: string) => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const removeUndefinedValues = <T extends Record<string, any>>(input: T) => {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
};

const SOURCE_LABELS: Record<ReturnShippingOptionSource, string> = {
  SHIPAID_RATES: 'SHIPAID rates',
  MERCHANT_CARRIER_ACCOUNT: 'Merchant carrier account',
};

const SHIPPING_OPTION_LABEL_MAX_LENGTH = 80;

const buildShippingOptionId = (
  source: ReturnShippingOptionSource,
  courierServiceId: string,
) => `${source}:${courierServiceId}`;

const DEFAULT_SHIPPING_COST_CONFIG: ShippingCostConfig = {
  responsibility: ShippingCostResponsibility.MERCHANT_PAYS,
};

const normalizeShippingOption = (
  option: any,
  index: number,
): ReturnShippingOptionSetting => {
  const source: ReturnShippingOptionSource =
    option?.source === 'MERCHANT_CARRIER_ACCOUNT'
      ? 'MERCHANT_CARRIER_ACCOUNT'
      : 'SHIPAID_RATES';
  const courierServiceId = String(option?.courierServiceId || '');
  const carrier = String(option?.carrier || '');
  const service = String(option?.service || '');
  const displayName = String(
    option?.displayName || [carrier, service].filter(Boolean).join(' ') || '',
  );

  return {
    id: buildShippingOptionId(source, courierServiceId || `${index}`),
    source,
    courierServiceId,
    carrier,
    service,
    displayName,
    enabled: option?.enabled ?? true,
    customerVisible: option?.customerVisible ?? true,
    sortOrder:
      typeof option?.sortOrder === 'number' ? option.sortOrder : index + 1,
    meta: option?.meta ?? null,
  };
};

const normalizeCarrierService = (
  service: any,
  fallbackSource: ReturnShippingOptionSource,
): ReturnCarrierService => {
  const source: ReturnShippingOptionSource =
    service?.source === 'MERCHANT_CARRIER_ACCOUNT'
      ? 'MERCHANT_CARRIER_ACCOUNT'
      : fallbackSource;
  const carrier = String(service?.carrier || '');
  const serviceName = String(service?.service || '');

  return {
    source,
    courierServiceId: String(service?.courierServiceId || service?.id || ''),
    carrier,
    service: serviceName,
    displayName: String(
      service?.displayName ||
        [carrier, serviceName].filter(Boolean).join(' ') ||
        '',
    ),
    active: service?.active,
    domestic: service?.domestic,
    international: service?.international,
    meta: service?.meta ?? null,
  };
};

const getCarrierServiceLabel = (service: ReturnCarrierService) =>
  service.displayName ||
  [service.carrier, service.service].filter(Boolean).join(' ') ||
  service.courierServiceId;

const getConfiguredShippingMethodName = (
  option: ReturnShippingOptionSetting,
) => {
  const service = option.service.trim();
  const carrier = option.carrier.trim();

  return service || carrier || option.displayName || 'Return shipping method';
};

const getCarrierServicesSourceMessage = (
  data: any,
  source: ReturnShippingOptionSource,
  error?: ApolloError,
) => {
  if (error) {
    return (
      apiErrorHandler(error)?.message ||
      `${SOURCE_LABELS[source]} services could not be loaded.`
    );
  }

  const sourceResult = (
    data?.easyshipReturnCarrierServices?.sources || []
  ).find((result: any) => result?.source === source);
  const status = String(sourceResult?.status || '').toUpperCase();

  if (sourceResult?.message) return String(sourceResult.message);
  if (status && status !== 'QUOTED') {
    return `${SOURCE_LABELS[source]} services could not be loaded.`;
  }

  return '';
};

type SearchableComboboxProps = {
  id: string;
  label: string;
  value: string;
  options: SearchablePickerOption[];
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  emptyText?: string;
};

const SearchableCombobox = ({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  emptyText = 'No matches found',
}: SearchableComboboxProps) => {
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const selectedOption =
    options.find((option) => option.value === value) || null;
  const normalizedQuery = query.toLowerCase().replace(/\s+/g, '');
  const filteredOptions =
    normalizedQuery === ''
      ? options
      : options.filter((option) =>
          [option.label, option.description]
            .filter(Boolean)
            .some((text) =>
              String(text)
                .toLowerCase()
                .replace(/\s+/g, '')
                .includes(normalizedQuery),
            ),
        );

  return (
    <Combobox
      value={selectedOption}
      onChange={(option: SearchablePickerOption | null) => {
        onChange(option?.value || '');
        setQuery('');
      }}
      disabled={disabled}
      as="div"
    >
      <Combobox.Label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
        {label}
      </Combobox.Label>
      <div className="relative">
        <Combobox.Button
          id={id}
          className={classNames(
            'relative flex w-full items-center justify-between gap-3 rounded-md border bg-white py-2 pl-3 pr-10 text-left text-sm leading-5 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500',
            disabled && 'bg-gray-50 text-gray-400',
            !disabled && 'border-gray-300 text-gray-900 hover:border-gray-400',
            disabled && 'border-gray-200',
          )}
          aria-label={`${label}: ${selectedOption?.label || placeholder}`}
        >
          <span
            className={classNames(
              'block truncate',
              !selectedOption && 'text-gray-400',
            )}
          >
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDownIcon
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
        </Combobox.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0 translate-y-1"
          afterEnter={() => searchInputRef.current?.focus()}
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options className="absolute z-40 mt-1 w-full overflow-hidden rounded-md bg-white text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <SearchIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <Combobox.Input
                  ref={searchInputRef}
                  className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm leading-5 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  displayValue={() => query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={`Search ${label.toLowerCase()}`}
                />
              </div>
            </div>
            <div className="max-h-56 overflow-auto py-1">
              {filteredOptions.length === 0 ? (
                <div className="relative cursor-default select-none py-2 px-3 text-gray-500">
                  {emptyText}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    className={({ active }) =>
                      classNames(
                        'relative cursor-pointer select-none py-2 pl-9 pr-3',
                        active ? 'bg-blue-600 text-white' : 'text-gray-900',
                      )
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className={classNames(
                              'block truncate',
                              selected && 'font-semibold',
                            )}
                          >
                            {option.label}
                          </span>
                          {option.recommended && (
                            <span
                              className={classNames(
                                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                active
                                  ? 'bg-white/20 text-white'
                                  : 'bg-blue-50 text-blue-700',
                              )}
                            >
                              Recommended
                            </span>
                          )}
                        </span>
                        {option.description && (
                          <span
                            className={classNames(
                              'block truncate text-xs',
                              active ? 'text-blue-100' : 'text-gray-500',
                            )}
                          >
                            {option.description}
                          </span>
                        )}
                        {selected && (
                          <span
                            className={classNames(
                              'absolute inset-y-0 left-0 flex items-center pl-2',
                              active ? 'text-white' : 'text-blue-600',
                            )}
                          >
                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </div>
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
};

const CarrierSettings = withNotification(
  ({ showNotification }: { showNotification: any }) => {
    const { storeId, storeProperties, refetchStoreProperties } = useStore();
    const [
      updateReturnShippingOptionsSettings,
      { loading: updatingShippingOptions },
    ] = useMutation(UPDATE_RETURN_SHIPPING_OPTIONS_SETTINGS);
    const {
      data: returnShippingOptionsSettingsData,
      loading: returnShippingOptionsLoading,
      error: returnShippingOptionsSettingsError,
      refetch: refetchReturnShippingOptionsSettings,
    } = useQuery(GET_RETURN_SHIPPING_OPTIONS_SETTINGS, {
      variables: { input: { storeId } },
      skip: !storeId,
      fetchPolicy: 'network-only',
    });
    const {
      data: shipAidCarrierServicesData,
      loading: shipAidCarrierServicesLoading,
      error: shipAidCarrierServicesError,
    } = useQuery(EASYSHIP_RETURN_CARRIER_SERVICES, {
      variables: { input: { storeId, sources: ['SHIPAID_RATES'] } },
      skip: !storeId,
      fetchPolicy: 'network-only',
    });
    const [
      loadShopifyReturnLocations,
      {
        data: shopifyReturnLocationsData,
        loading: shopifyReturnLocationsLoading,
        error: shopifyReturnLocationsError,
      },
    ] = useLazyQuery(SHOPIFY_RETURN_LOCATIONS, {
      fetchPolicy: 'network-only',
    });
    const [shippingOptions, setShippingOptions] = useState<
      ReturnShippingOptionSetting[]
    >([]);
    const [fallbackBehavior, setFallbackBehavior] = useState(
      'FAIL_IF_SELECTED_UNAVAILABLE',
    );
    const [selectedShipAidCarrier, setSelectedShipAidCarrier] = useState('');
    const [selectedShipAidServiceId, setSelectedShipAidServiceId] =
      useState('');
    const [
      hasAppliedShipAidRecommendation,
      setHasAppliedShipAidRecommendation,
    ] = useState(false);
    const [draggedShippingOptionId, setDraggedShippingOptionId] = useState<
      string | null
    >(null);
    const [expandedShippingOptionIds, setExpandedShippingOptionIds] = useState<
      string[]
    >([]);
    const hasInitializedShippingOptionExpansion = useRef(false);
    const previousStoreIdRef = useRef(storeId);
    const activeStoreIdRef = useRef(storeId);
    const hydratedReturnLocationsStoreIdRef = useRef<string | null>(null);
    const hydratedReturnLabelsStoreIdRef = useRef<string | null>(null);
    const hydratedShippingCostStoreIdRef = useRef<string | null>(null);
    const hydratedShippingOptionsStoreIdRef = useRef<string | null>(null);
    const [labelFormat, setLabelFormat] = useState('PDF');
    const [labelPageSize, setLabelPageSize] = useState('4x6');
    const [defaultWeightOz, setDefaultWeightOz] = useState('');
    const [defaultLengthIn, setDefaultLengthIn] = useState('');
    const [defaultWidthIn, setDefaultWidthIn] = useState('');
    const [defaultHeightIn, setDefaultHeightIn] = useState('');
    const [protectedOrdersShippingCost, setProtectedOrdersShippingCost] =
      useState<ShippingCostConfig>(DEFAULT_SHIPPING_COST_CONFIG);
    const [unprotectedOrdersShippingCost, setUnprotectedOrdersShippingCost] =
      useState<ShippingCostConfig>(DEFAULT_SHIPPING_COST_CONFIG);

    const [returnLocations, setReturnLocations] = useState<ReturnLocation[]>(
      [],
    );
    const [hasSavedReturnLocations, setHasSavedReturnLocations] =
      useState(false);
    const [hasAppliedShopifyLocations, setHasAppliedShopifyLocations] =
      useState(false);
    const [isAddLocationModalOpen, setIsAddLocationModalOpen] = useState(false);
    const [editingLocationId, setEditingLocationId] = useState<string | null>(
      null,
    );
    const [locationFormErrors, setLocationFormErrors] =
      useState<ReturnLocationValidationErrors>({});
    const [locationFormBaseline, setLocationFormBaseline] = useState('');

    // Temporary state for form input
    const [locationForm, setLocationForm] = useState<ReturnLocation>({
      id: '',
      label: '',
      street: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'United States',
      country_alpha2: 'US',
      phone: '',
      email: '',
      isDefault: false,
      source: 'MANUAL',
    });
    const hasLocationDraftChanges = useMemo(
      () =>
        isAddLocationModalOpen &&
        JSON.stringify(locationForm) !== locationFormBaseline,
      [isAddLocationModalOpen, locationForm, locationFormBaseline],
    );

    useEffect(() => {
      activeStoreIdRef.current = storeId;
      if (previousStoreIdRef.current === storeId) return;

      previousStoreIdRef.current = storeId;
      hydratedReturnLocationsStoreIdRef.current = null;
      hydratedReturnLabelsStoreIdRef.current = null;
      hydratedShippingCostStoreIdRef.current = null;
      hydratedShippingOptionsStoreIdRef.current = null;
      hasInitializedShippingOptionExpansion.current = false;
      setExpandedShippingOptionIds([]);
      setShippingOptions([]);
      setFallbackBehavior('FAIL_IF_SELECTED_UNAVAILABLE');
      setSelectedShipAidCarrier('');
      setSelectedShipAidServiceId('');
      setHasAppliedShipAidRecommendation(false);
      setDraggedShippingOptionId(null);
      setLabelFormat('PDF');
      setLabelPageSize('4x6');
      setDefaultWeightOz('');
      setDefaultLengthIn('');
      setDefaultWidthIn('');
      setDefaultHeightIn('');
      setProtectedOrdersShippingCost(DEFAULT_SHIPPING_COST_CONFIG);
      setUnprotectedOrdersShippingCost(DEFAULT_SHIPPING_COST_CONFIG);
      setReturnLocations([]);
      setHasSavedReturnLocations(false);
      setHasAppliedShopifyLocations(false);
      setIsAddLocationModalOpen(false);
      setEditingLocationId(null);
      setLocationFormErrors({});
      setLocationFormBaseline('');
    }, [storeId]);

    useEffect(() => {
      if (
        !storeId ||
        !storeProperties ||
        storeProperties.id !== storeId ||
        hydratedReturnLocationsStoreIdRef.current === storeId
      ) {
        return;
      }

      let foundSavedLocation = false;

      if (storeProperties.policySettings) {
        const settings = storeProperties.policySettings;

        // Load Operational Controls data
        if (settings.operationalControls) {
          const ops = settings.operationalControls;
          if (ops.logistics) {
            const log = ops.logistics;

            if (log.returnLocations && log.returnLocations.length > 0) {
              setReturnLocations(
                log.returnLocations.map((location: any, index: number) =>
                  normalizeReturnLocationForSave(
                    location,
                    `return-location-${index}`,
                  ),
                ),
              );
              foundSavedLocation = true;
            } else if (hasSavedReturnAddress(log.returnAddress)) {
              // Migrate single address to list
              setReturnLocations([
                normalizeReturnLocationForSave({
                  id: 'primary-default',
                  label: 'Primary Warehouse',
                  ...log.returnAddress,
                  isDefault: true,
                  source: 'MANUAL',
                }),
              ]);
              foundSavedLocation = true;
            }
          }
        }
      }

      if (!foundSavedLocation) {
        setReturnLocations([]);
        setHasAppliedShopifyLocations(false);
      }
      setHasSavedReturnLocations(foundSavedLocation);
      if (foundSavedLocation) setHasAppliedShopifyLocations(false);
      hydratedReturnLocationsStoreIdRef.current = storeId;
    }, [storeId, storeProperties]);

    useEffect(() => {
      if (
        !storeId ||
        !storeProperties ||
        storeProperties.id !== storeId ||
        hydratedReturnLabelsStoreIdRef.current === storeId
      ) {
        return;
      }

      setLabelFormat('PDF');
      setLabelPageSize('4x6');
      setDefaultWeightOz('');
      setDefaultLengthIn('');
      setDefaultWidthIn('');
      setDefaultHeightIn('');

      const returnLabels = (storeProperties.features as any)?.returns
        ?.returnLabels;
      if (returnLabels) {
        setLabelFormat(returnLabels.labelFormat || 'PDF');
        setLabelPageSize(returnLabels.labelPageSize || '4x6');
        setDefaultWeightOz(
          returnLabels.defaultWeightOz != null
            ? String(returnLabels.defaultWeightOz)
            : '',
        );
        setDefaultLengthIn(
          returnLabels.defaultLengthIn != null
            ? String(returnLabels.defaultLengthIn)
            : '',
        );
        setDefaultWidthIn(
          returnLabels.defaultWidthIn != null
            ? String(returnLabels.defaultWidthIn)
            : '',
        );
        setDefaultHeightIn(
          returnLabels.defaultHeightIn != null
            ? String(returnLabels.defaultHeightIn)
            : '',
        );
      }
      hydratedReturnLabelsStoreIdRef.current = storeId;
    }, [storeId, storeProperties]);

    useEffect(() => {
      if (
        !storeId ||
        !storeProperties ||
        storeProperties.id !== storeId ||
        hydratedShippingCostStoreIdRef.current === storeId
      ) {
        return;
      }

      setProtectedOrdersShippingCost(DEFAULT_SHIPPING_COST_CONFIG);
      setUnprotectedOrdersShippingCost(DEFAULT_SHIPPING_COST_CONFIG);

      const shippingCost =
        storeProperties.incentiveSettings?.refundFallback?.shippingCost;

      setProtectedOrdersShippingCost(
        shippingCost?.protectedOrders || DEFAULT_SHIPPING_COST_CONFIG,
      );
      setUnprotectedOrdersShippingCost(
        shippingCost?.unprotectedOrders || DEFAULT_SHIPPING_COST_CONFIG,
      );
      hydratedShippingCostStoreIdRef.current = storeId;
    }, [storeId, storeProperties]);

    const shopifyLocations = useMemo(() => {
      const response = shopifyReturnLocationsData?.shopifyReturnLocations;
      return !response || response.storeId !== storeId
        ? []
        : response.locations || [];
    }, [shopifyReturnLocationsData, storeId]);

    useEffect(() => {
      const response =
        returnShippingOptionsSettingsData?.getReturnShippingOptionsSettings;
      if (
        !storeId ||
        !response ||
        response.storeId !== storeId ||
        returnShippingOptionsLoading ||
        returnShippingOptionsSettingsError ||
        !Array.isArray(response.enabledOptions) ||
        hydratedShippingOptionsStoreIdRef.current === storeId
      ) {
        return;
      }
      setFallbackBehavior(
        String(response.fallbackBehavior || 'FAIL_IF_SELECTED_UNAVAILABLE'),
      );
      const options = response.enabledOptions as unknown[];

      const normalizedOptions = options.map(normalizeShippingOption);

      setShippingOptions(normalizedOptions);
      if (!hasInitializedShippingOptionExpansion.current) {
        setExpandedShippingOptionIds(
          normalizedOptions.map((option) => option.id),
        );
        hasInitializedShippingOptionExpansion.current = true;
      }
      hydratedShippingOptionsStoreIdRef.current = storeId;
    }, [
      returnShippingOptionsSettingsData,
      returnShippingOptionsLoading,
      returnShippingOptionsSettingsError,
      storeId,
    ]);

    useEffect(() => {
      if (
        hasSavedReturnLocations ||
        hasAppliedShopifyLocations ||
        returnLocations.length > 0 ||
        shopifyLocations.length === 0
      ) {
        return;
      }

      setReturnLocations(normalizeShopifyReturnLocations(shopifyLocations));
      setHasAppliedShopifyLocations(true);
    }, [
      hasSavedReturnLocations,
      hasAppliedShopifyLocations,
      returnLocations.length,
      shopifyLocations,
    ]);

    const shipAidCarrierServices: ReturnCarrierService[] = useMemo(() => {
      const response =
        shipAidCarrierServicesData?.easyshipReturnCarrierServices;
      if (!response || response.storeId !== storeId) return [];

      return (response.services || []).map((service: any) =>
        normalizeCarrierService(service, 'SHIPAID_RATES'),
      );
    }, [shipAidCarrierServicesData, storeId]);

    const shipAidCarrierOptions = useMemo(
      () => getCarrierOptions(shipAidCarrierServices),
      [shipAidCarrierServices],
    );

    const shipAidServiceOptions = useMemo(
      () =>
        getServiceOptionsForCarrier(
          shipAidCarrierServices,
          selectedShipAidCarrier,
        ),
      [selectedShipAidCarrier, shipAidCarrierServices],
    );

    useEffect(() => {
      if (
        hasAppliedShipAidRecommendation ||
        selectedShipAidCarrier ||
        selectedShipAidServiceId
      ) {
        return;
      }

      const recommendedService = getRecommendedServiceSelection(
        shipAidCarrierServices,
      );
      if (!recommendedService) return;

      setSelectedShipAidCarrier(recommendedService.carrier);
      setSelectedShipAidServiceId(recommendedService.courierServiceId);
      setHasAppliedShipAidRecommendation(true);
    }, [
      hasAppliedShipAidRecommendation,
      selectedShipAidCarrier,
      selectedShipAidServiceId,
      shipAidCarrierServices,
    ]);

    useEffect(() => {
      if (
        selectedShipAidCarrier &&
        shipAidCarrierOptions.length > 0 &&
        !shipAidCarrierOptions.some(
          (option) => option.value === selectedShipAidCarrier,
        )
      ) {
        setSelectedShipAidCarrier('');
        setSelectedShipAidServiceId('');
      }
    }, [selectedShipAidCarrier, shipAidCarrierOptions]);

    useEffect(() => {
      if (
        selectedShipAidServiceId &&
        shipAidServiceOptions.length > 0 &&
        !shipAidServiceOptions.some(
          (option) => option.value === selectedShipAidServiceId,
        )
      ) {
        setSelectedShipAidServiceId('');
      }
    }, [selectedShipAidServiceId, shipAidServiceOptions]);

    const shippingFormState = useMemo(
      () => ({
        shippingOptions,
        labelFormat,
        labelPageSize,
        defaultWeightOz,
        defaultLengthIn,
        defaultWidthIn,
        defaultHeightIn,
        protectedOrdersShippingCost,
        unprotectedOrdersShippingCost,
        returnLocations,
        locationDraft: hasLocationDraftChanges
          ? { editingLocationId, locationForm }
          : null,
      }),
      [
        defaultHeightIn,
        defaultLengthIn,
        defaultWeightOz,
        defaultWidthIn,
        labelFormat,
        labelPageSize,
        editingLocationId,
        hasLocationDraftChanges,
        locationForm,
        protectedOrdersShippingCost,
        returnLocations,
        shippingOptions,
        unprotectedOrdersShippingCost,
      ],
    );
    const shippingHydrationKey = useMemo(
      () =>
        JSON.stringify({
          storeId,
          storePropertiesReady: Boolean(
            storeId && storeProperties?.id === storeId,
          ),
          shippingOptionsReady: Boolean(
            storeId &&
              returnShippingOptionsSettingsData
                ?.getReturnShippingOptionsSettings?.storeId === storeId,
          ),
        }),
      [
        returnShippingOptionsSettingsData?.getReturnShippingOptionsSettings
          ?.storeId,
        storeId,
        storeProperties?.id,
      ],
    );
    const { hasUnsavedChanges, saveStatus, markSaved, markSaveFailed } =
      useSettingsSaveState(shippingFormState, shippingHydrationKey);

    const shipAidCarrierServicesMessage =
      !shipAidCarrierServicesLoading && shipAidCarrierServices.length === 0
        ? getCarrierServicesSourceMessage(
            shipAidCarrierServicesData,
            'SHIPAID_RATES',
            shipAidCarrierServicesError,
          )
        : '';
    const selectedShipAidServiceOption = shipAidServiceOptions.find(
      (option) => option.value === selectedShipAidServiceId,
    );
    const orderedShippingOptions = useMemo(
      () => getOrderedShippingOptions(shippingOptions),
      [shippingOptions],
    );

    const getNextSortOrder = (options: ReturnShippingOptionSetting[]) =>
      options.reduce((max, option) => Math.max(max, option.sortOrder || 0), 0) +
      1;

    const handleAddShippingOption = (source: ReturnShippingOptionSource) => {
      if (source !== 'SHIPAID_RATES') return;

      if (!selectedShipAidCarrier) {
        showNotification(
          NotificationTypes.error,
          'Select a carrier before adding a return shipping option',
        );
        return;
      }

      const service = shipAidCarrierServices.find(
        (item) =>
          item.carrier === selectedShipAidCarrier &&
          item.courierServiceId === selectedShipAidServiceId,
      );

      if (!service) {
        showNotification(
          NotificationTypes.error,
          'Select a service before adding a return shipping option',
        );
        return;
      }

      const alreadyConfigured = shippingOptions.some(
        (option) =>
          option.source === source &&
          option.courierServiceId === service.courierServiceId,
      );

      if (alreadyConfigured) {
        showNotification(
          NotificationTypes.error,
          'That courier service is already configured',
        );
        return;
      }

      setShippingOptions((currentOptions) => {
        const nextOption = normalizeShippingOption(
          {
            ...service,
            displayName: getCarrierServiceLabel(service),
            customerVisible: true,
            sortOrder: getNextSortOrder(currentOptions),
          },
          currentOptions.length,
        );

        setExpandedShippingOptionIds((expandedIds) => [
          ...expandedIds.filter((id) => id !== nextOption.id),
          nextOption.id,
        ]);

        return [...currentOptions, nextOption];
      });
      setSelectedShipAidServiceId('');
    };

    const handleUpdateShippingOption = (
      id: string,
      patch: Partial<ReturnShippingOptionSetting>,
    ) => {
      setShippingOptions((currentOptions) =>
        currentOptions.map((option) =>
          option.id === id ? { ...option, ...patch } : option,
        ),
      );
    };

    const handleRemoveShippingOption = (id: string) => {
      setShippingOptions((currentOptions) =>
        currentOptions.filter((option) => option.id !== id),
      );
      setExpandedShippingOptionIds((expandedIds) =>
        expandedIds.filter((expandedId) => expandedId !== id),
      );
    };

    const toggleShippingOptionExpanded = (id: string) => {
      setExpandedShippingOptionIds((expandedIds) =>
        expandedIds.includes(id)
          ? expandedIds.filter((expandedId) => expandedId !== id)
          : [...expandedIds, id],
      );
    };

    const getShippingOptionsPayload = () =>
      shippingOptions
        .slice()
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .filter((option) => option.courierServiceId.trim())
        .map((option, index) =>
          removeUndefinedValues({
            source: option.source,
            courierServiceId: option.courierServiceId.trim(),
            carrier: option.carrier.trim() || undefined,
            service: option.service.trim() || undefined,
            displayName:
              option.displayName.trim() ||
              [option.carrier, option.service].filter(Boolean).join(' ') ||
              undefined,
            enabled: Boolean(option.enabled),
            customerVisible: Boolean(option.customerVisible),
            sortOrder: Number.isFinite(Number(option.sortOrder))
              ? Number(option.sortOrder)
              : index + 1,
            meta: option.meta ?? undefined,
          }),
        );

    const handleSyncShopifyLocations = async () => {
      if (!storeId) return;
      const requestedStoreId = storeId;

      try {
        const response = await loadShopifyReturnLocations({
          variables: {
            input: { storeId: requestedStoreId },
          },
        });
        const result = response.data?.shopifyReturnLocations;
        if (
          activeStoreIdRef.current !== requestedStoreId ||
          !result ||
          result.storeId !== requestedStoreId
        ) {
          return;
        }

        const syncedLocations = result.locations || [];
        const nextLocations = normalizeShopifyReturnLocations(syncedLocations);

        if (nextLocations.length === 0) {
          showNotification(
            NotificationTypes.error,
            'No active Shopify warehouse locations were found.',
          );
          return;
        }

        setReturnLocations(nextLocations);
        setHasAppliedShopifyLocations(true);
        setHasSavedReturnLocations(false);
        showNotification(
          NotificationTypes.success,
          'Shopify warehouse locations synced. Save changes to keep them.',
        );
      } catch (error) {
        if (activeStoreIdRef.current !== requestedStoreId) return;

        const newError = apiErrorHandler(error as ApolloError);
        showNotification(
          NotificationTypes.error,
          newError?.message ||
            'Shopify warehouse locations could not be synced.',
        );
      }
    };

    const closeLocationForm = () => {
      setIsAddLocationModalOpen(false);
      setLocationFormErrors({});
      setLocationFormBaseline('');
    };

    const requestCloseLocationForm = () => {
      if (
        hasLocationDraftChanges &&
        !window.confirm('Discard the unsaved changes to this return location?')
      ) {
        return;
      }

      closeLocationForm();
    };

    const openLocationForm = (
      location: ReturnLocation,
      locationId: string | null,
      errors: ReturnLocationValidationErrors = {},
    ) => {
      setLocationForm(location);
      setEditingLocationId(locationId);
      setLocationFormErrors(errors);
      setLocationFormBaseline(JSON.stringify(location));
      setIsAddLocationModalOpen(true);
    };

    const updateLocationFormField = <Key extends keyof ReturnLocation>(
      field: Key,
      value: ReturnLocation[Key],
    ) => {
      setLocationForm((current) => ({ ...current, [field]: value }));
      setLocationFormErrors((current) => {
        if (!(field in current)) return current;
        const next = { ...current };
        delete next[field as keyof ReturnLocationValidationErrors];
        return next;
      });
    };

    const upsertReturnLocation = (
      locations: ReturnLocation[],
      location: ReturnLocation,
      locationId: string | null,
    ) => {
      let updatedLocations = location.isDefault
        ? locations.map((item) => ({ ...item, isDefault: false }))
        : [...locations];

      updatedLocations = locationId
        ? updatedLocations.map((item) =>
            item.id === locationId ? location : item,
          )
        : [...updatedLocations, location];

      if (
        updatedLocations.length > 0 &&
        !updatedLocations.some((item) => item.isDefault)
      ) {
        updatedLocations = updatedLocations.map((item, index) =>
          index === 0 ? { ...item, isDefault: true } : item,
        );
      }

      return updatedLocations;
    };

    const loadedShippingSettings =
      returnShippingOptionsSettingsData?.getReturnShippingOptionsSettings;
    const shippingSettingsHydrating = Boolean(
      !returnShippingOptionsSettingsError &&
        loadedShippingSettings?.storeId === storeId &&
        Array.isArray(loadedShippingSettings?.enabledOptions) &&
        hydratedShippingOptionsStoreIdRef.current !== storeId,
    );
    const shippingSettingsPending =
      returnShippingOptionsLoading || shippingSettingsHydrating;
    const shippingSettingsReady = Boolean(
      storeId &&
        !returnShippingOptionsLoading &&
        !returnShippingOptionsSettingsError &&
        loadedShippingSettings?.storeId === storeId &&
        Array.isArray(loadedShippingSettings.enabledOptions) &&
        hydratedShippingOptionsStoreIdRef.current === storeId,
    );

    const handleSave = async (): Promise<boolean> => {
      if (!storeProperties) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          'Store settings are still loading. Please try again in a moment.',
        );
        return false;
      }

      if (!storeProperties || storeProperties.id !== storeId) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          'The selected store is still loading. Please try again in a moment.',
        );
        return false;
      }

      if (!shippingSettingsReady) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          'Return shipping settings must finish loading before saving. Retry if they could not be loaded.',
        );
        return false;
      }

      const validationError = getShippingSettingsValidationError({
        defaultWeightOz,
        defaultLengthIn,
        defaultWidthIn,
        defaultHeightIn,
        protectedOrdersShippingCost,
        unprotectedOrdersShippingCost,
      });
      if (validationError) {
        markSaveFailed();
        showNotification(NotificationTypes.error, validationError);
        return false;
      }

      let locationsForSave = returnLocations;

      if (hasLocationDraftChanges) {
        const draftErrors = getReturnLocationValidationErrors(locationForm);
        if (Object.keys(draftErrors).length > 0) {
          setLocationFormErrors(draftErrors);
          markSaveFailed();
          showNotification(
            NotificationTypes.error,
            Object.values(draftErrors)[0] ||
              'Review the highlighted return location fields.',
          );
          return false;
        }

        const sanitizedDraft = normalizeReturnLocationForSave(locationForm);
        locationsForSave = upsertReturnLocation(
          returnLocations,
          sanitizedDraft,
          editingLocationId,
        );
        setReturnLocations(locationsForSave);
        closeLocationForm();
      }

      const invalidLocationIndex = locationsForSave.findIndex(
        (location) =>
          Object.keys(getReturnLocationValidationErrors(location)).length > 0,
      );

      if (invalidLocationIndex >= 0) {
        const invalidLocation = locationsForSave[invalidLocationIndex];
        const invalidLocationErrors =
          getReturnLocationValidationErrors(invalidLocation);
        openLocationForm(
          invalidLocation,
          invalidLocation.id,
          invalidLocationErrors,
        );
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          Object.values(invalidLocationErrors)[0] ||
            'Review the highlighted return location fields.',
        );
        return false;
      }

      const sanitizedReturnLocations = locationsForSave.map((location, index) =>
        normalizeReturnLocationForSave(
          location,
          location.id || `return-location-${index}`,
        ),
      );

      const policySettingsPatch = {
        operationalControls: {
          logistics: {
            returnLocations: sanitizedReturnLocations,
            returnAddress: (() => {
              const defaultLoc =
                sanitizedReturnLocations.find((loc) => loc.isDefault) ||
                sanitizedReturnLocations[0];
              return {
                street: defaultLoc?.street || '',
                street2: defaultLoc?.street2 || '',
                // Null deletes legacy aliases from the merged address patch.
                line_2: null,
                address2: null,
                address_2: null,
                line2: null,
                city: defaultLoc?.city || '',
                state: defaultLoc?.state || '',
                zip: defaultLoc?.zip || '',
                country: defaultLoc?.country || '',
                country_alpha2: defaultLoc?.country_alpha2 || '',
                phone: defaultLoc?.phone || '',
                email: defaultLoc?.email || '',
              };
            })(),
          },
        },
      };

      const returnLabelSettingsPatch = {
        labelFormat: labelFormat.trim() || null,
        labelPageSize: labelPageSize.trim() || null,
        defaultWeightOz: parseOptionalNumber(defaultWeightOz) ?? null,
        defaultLengthIn: parseOptionalNumber(defaultLengthIn) ?? null,
        defaultWidthIn: parseOptionalNumber(defaultWidthIn) ?? null,
        defaultHeightIn: parseOptionalNumber(defaultHeightIn) ?? null,
      };
      const incentiveSettingsPatch = {
        refundFallback: {
          shippingCost: {
            protectedOrders: protectedOrdersShippingCost,
            unprotectedOrders: unprotectedOrdersShippingCost,
          },
        },
      };

      try {
        await updateReturnShippingOptionsSettings({
          variables: {
            input: {
              storeId,
              policySettingsPatch,
              incentiveSettingsPatch,
              returnLabelSettingsPatch,
              enabledOptions: getShippingOptionsPayload(),
            },
          },
        });
        markSaved();
        showNotification(NotificationTypes.success, 'Return shipping saved.');
        refetchStoreProperties && refetchStoreProperties();
        refetchReturnShippingOptionsSettings?.();
        return true;
      } catch (error) {
        markSaveFailed();
        const newError = apiErrorHandler(error as ApolloError);
        showNotification(NotificationTypes.error, newError?.message);
        return false;
      }
    };

    const generateId = () => Math.random().toString(36).substr(2, 9);

    const handleAddLocation = () => {
      const nextLocation: ReturnLocation = {
        id: generateId(),
        label: '',
        street: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States',
        country_alpha2: 'US',
        phone: '',
        email: '',
        isDefault: returnLocations.length === 0, // Make default if it's the first one
        source: 'MANUAL',
      };
      openLocationForm(nextLocation, null);
    };

    const handleEditLocation = (loc: ReturnLocation) => {
      openLocationForm(normalizeReturnLocationForSave(loc), loc.id);
    };

    const handleDeleteLocation = (id: string) => {
      if (window.confirm('Are you sure you want to remove this location?')) {
        const updated = returnLocations.filter((l) => l.id !== id);
        // Ensure at least one default remains if possible
        if (updated.length > 0 && !updated.some((l) => l.isDefault)) {
          updated[0].isDefault = true;
        }
        setReturnLocations(updated);
      }
    };

    const saveLocation = () => {
      const errors = getReturnLocationValidationErrors(locationForm);
      if (Object.keys(errors).length > 0) {
        setLocationFormErrors(errors);
        return;
      }

      const sanitizedLocation = normalizeReturnLocationForSave(locationForm);
      const updatedLocations = upsertReturnLocation(
        returnLocations,
        sanitizedLocation,
        editingLocationId,
      );

      setReturnLocations(updatedLocations);
      closeLocationForm();
    };

    const visibleShippingOptionsCount = shippingOptions.filter(
      isShippingOptionAvailable,
    ).length;
    const usesStrictFallback =
      fallbackBehavior === 'FAIL_IF_SELECTED_UNAVAILABLE';
    const primaryReturnLocation =
      returnLocations.find((location) => location.isDefault) ||
      returnLocations[0];
    const labelOutputSummary = `${labelFormat || 'PDF'} · ${
      labelPageSize || '4x6'
    }`;

    return (
      <SettingsViewWrapper
        sectionId="returns-settings"
        activeNav="resolution-portal/returns/carriers"
      >
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleSave}
        />
        <div className="w-full h-full bg-[#f6f6f7]">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
            <ReturnsSettingsTabs activeTab="carriers" />

            {/* Header */}
            <div className="sticky top-0 z-20 -mx-2 flex flex-col gap-4 border-b border-gray-200 bg-[#f6f6f7]/95 px-2 py-3 backdrop-blur-sm md:flex-row md:items-start md:justify-between">
              <div>
                <SharedResolutionSettingsNotice />
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Shipping
                </h1>
                <p className="text-gray-500 mt-1 max-w-2xl text-sm">
                  Set carrier methods, labels, packages, and return locations.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {hasUnsavedChanges && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-amber-700"
                  >
                    Unsaved changes
                  </span>
                )}
                {saveStatus === 'saved' && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-green-700"
                  >
                    Saved
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-red-700"
                  >
                    Not saved
                  </span>
                )}
                <span
                  className={classNames(
                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
                    visibleShippingOptionsCount
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-gray-100 text-gray-700 border-gray-200',
                  )}
                >
                  <span
                    className={classNames(
                      'w-1.5 h-1.5 rounded-full mr-1.5',
                      visibleShippingOptionsCount
                        ? 'bg-green-500'
                        : 'bg-gray-400',
                    )}
                  />
                  {visibleShippingOptionsCount ? 'Live' : 'Setup needed'}
                </span>
                <Button
                  size={SizeEnum.sm}
                  onClick={handleSave}
                  isLoading={updatingShippingOptions}
                  disabled={!shippingSettingsReady}
                >
                  {updatingShippingOptions ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>

            {!shippingSettingsReady && (
              <div
                role={shippingSettingsPending ? 'status' : 'alert'}
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
              >
                {shippingSettingsPending ? (
                  'Loading return shipping settings…'
                ) : (
                  <>
                    <p>
                      Return shipping settings could not be loaded. Retry before saving.
                    </p>
                    <Button
                      size={SizeEnum.sm}
                      variant="outlined"
                      className="mt-2"
                      onClick={() => {
                        void refetchReturnShippingOptionsSettings().catch(() => undefined);
                      }}
                    >
                      Retry shipping settings
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 2xl:grid-cols-12 gap-6">
              <div className="2xl:col-span-8 space-y-6">
                <ReturnLabelSettings
                  protectedOrders={protectedOrdersShippingCost}
                  unprotectedOrders={unprotectedOrdersShippingCost}
                  onProtectedOrdersChange={setProtectedOrdersShippingCost}
                  onUnprotectedOrdersChange={setUnprotectedOrdersShippingCost}
                />

                {/* Carrier methods */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <TruckIcon className="w-5 h-5 text-gray-400" />
                      Carrier methods
                    </h3>
                  </div>
                  <div className="p-6 space-y-8">
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 gap-4">
                        <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-5 space-y-5">
                          <div className="flex gap-4">
                            <div className="p-2 bg-blue-100 rounded-lg h-fit">
                              <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="text-base font-bold text-gray-900">
                                SHIPAID rates
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {
                                  'Generate return labels with discounted rates.'
                                }
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] gap-3 lg:items-end">
                            <SearchableCombobox
                              id="shipaid-carrier"
                              label="Carrier"
                              value={selectedShipAidCarrier}
                              options={shipAidCarrierOptions}
                              onChange={(carrier) => {
                                setSelectedShipAidCarrier(carrier);
                                setSelectedShipAidServiceId('');
                              }}
                              placeholder={
                                shipAidCarrierServicesLoading
                                  ? 'Loading carriers'
                                  : shipAidCarrierOptions.length === 0
                                  ? 'No carriers available'
                                  : 'Search carriers'
                              }
                              disabled={
                                shipAidCarrierServicesLoading ||
                                shipAidCarrierOptions.length === 0
                              }
                              emptyText="No carriers found"
                            />
                            <SearchableCombobox
                              id="shipaid-service"
                              label="Service"
                              value={selectedShipAidServiceId}
                              options={shipAidServiceOptions}
                              onChange={setSelectedShipAidServiceId}
                              placeholder={
                                selectedShipAidCarrier
                                  ? 'Search services'
                                  : 'Select carrier first'
                              }
                              disabled={
                                shipAidCarrierServicesLoading ||
                                !selectedShipAidCarrier ||
                                shipAidServiceOptions.length === 0
                              }
                              emptyText="No services found"
                            />
                            <div className="flex lg:pb-[1px]">
                              <Button
                                size={SizeEnum.sm}
                                variant="outlined"
                                onClick={() =>
                                  handleAddShippingOption('SHIPAID_RATES')
                                }
                                disabled={
                                  shipAidCarrierServicesLoading ||
                                  !selectedShipAidCarrier ||
                                  !selectedShipAidServiceId
                                }
                              >
                                Add
                              </Button>
                            </div>
                          </div>
                          {selectedShipAidServiceOption?.recommended && (
                            <p className="rounded-md border border-blue-100 bg-white px-3 py-2 text-xs text-gray-600">
                              {
                                'USPS Ground Advantage is the default for most US returns.'
                              }
                            </p>
                          )}
                          <div>
                            {shipAidCarrierServicesMessage && (
                              <p className="mt-2 text-xs text-red-600">
                                {shipAidCarrierServicesMessage}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900">
                              Customer methods
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {'Choose the methods customers can use.'}
                            </p>
                          </div>
                          {returnShippingOptionsLoading && (
                            <span className="text-xs font-semibold text-gray-500">
                              Loading settings
                            </span>
                          )}
                        </div>

                        <div
                          className={classNames(
                            'border-b px-5 py-4',
                            usesStrictFallback
                              ? 'border-gray-100 bg-gray-50'
                              : 'border-amber-200 bg-amber-50',
                          )}
                        >
                          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                            Method fallback
                          </p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">
                            {usesStrictFallback
                              ? 'Stop if the selected method is unavailable'
                              : 'Unsupported legacy fallback saved'}
                          </p>
                          <p className="mt-1 text-xs leading-5 text-gray-600">
                            {usesStrictFallback
                              ? 'The system will not substitute a different carrier method.'
                              : 'Automatic fallback routing is not supported reliably yet. This saved setting is preserved without changes.'}
                          </p>
                        </div>

                        {shippingOptions.length === 0 ? (
                          <div className="px-5 py-8 text-center border-dashed">
                            <p className="text-sm text-gray-500">
                              No methods added.
                            </p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {orderedShippingOptions.map((option, index) => {
                              const isExpanded =
                                expandedShippingOptionIds.includes(option.id);
                              const isAvailable =
                                isShippingOptionAvailable(option);
                              const detailsId = `shipping-option-details-${option.id}`;

                              return (
                                <div
                                  key={option.id}
                                  onDragOver={(event) => {
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={(event) => {
                                    event.preventDefault();
                                    const draggedId =
                                      event.dataTransfer.getData(
                                        'text/plain',
                                      ) || draggedShippingOptionId;

                                    if (!draggedId) return;

                                    setShippingOptions((currentOptions) =>
                                      getReorderedShippingOptions(
                                        currentOptions,
                                        draggedId,
                                        option.id,
                                      ),
                                    );
                                    setDraggedShippingOptionId(null);
                                  }}
                                  className={classNames(
                                    'bg-white transition-colors',
                                    draggedShippingOptionId === option.id &&
                                      'opacity-60',
                                  )}
                                >
                                  <div className="flex items-start gap-3 px-5 py-4">
                                    <button
                                      type="button"
                                      draggable
                                      onDragStart={(event) => {
                                        setDraggedShippingOptionId(option.id);
                                        event.dataTransfer.effectAllowed =
                                          'move';
                                        event.dataTransfer.setData(
                                          'text/plain',
                                          option.id,
                                        );
                                      }}
                                      onDragEnd={() =>
                                        setDraggedShippingOptionId(null)
                                      }
                                      className="mt-1 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 cursor-grab active:cursor-grabbing"
                                      aria-label={`Drag to reorder ${getConfiguredShippingMethodName(
                                        option,
                                      )}`}
                                      title="Drag to reorder"
                                    >
                                      <MenuAlt2Icon className="h-5 w-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleShippingOptionExpanded(option.id)
                                      }
                                      className="flex min-w-0 flex-1 items-start gap-3 text-left"
                                      aria-expanded={isExpanded}
                                      aria-controls={detailsId}
                                    >
                                      <span className="mt-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-bold text-gray-600">
                                        {index + 1}
                                      </span>
                                      <span className="min-w-0 flex-1">
                                        <span className="flex flex-wrap items-center gap-2">
                                          <span className="truncate text-sm font-semibold text-gray-900">
                                            {getConfiguredShippingMethodName(
                                              option,
                                            )}
                                          </span>
                                          <span
                                            className={classNames(
                                              'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                              isAvailable
                                                ? 'bg-green-50 text-green-700'
                                                : 'bg-gray-100 text-gray-500',
                                            )}
                                          >
                                            {isAvailable
                                              ? 'Available'
                                              : 'Unavailable'}
                                          </span>
                                        </span>
                                        <span className="mt-1 block truncate text-xs text-gray-500">
                                          {[
                                            option.carrier &&
                                              `Carrier: ${option.carrier}`,
                                            `Rate source: ${
                                              SOURCE_LABELS[option.source]
                                            }`,
                                            option.displayName &&
                                              `Customer label: ${option.displayName}`,
                                          ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                        </span>
                                      </span>
                                      {isExpanded ? (
                                        <ChevronDownIcon className="mt-1 h-5 w-5 shrink-0 text-gray-400" />
                                      ) : (
                                        <ChevronRightIcon className="mt-1 h-5 w-5 shrink-0 text-gray-400" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveShippingOption(option.id)
                                      }
                                      className="mt-1 rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                      aria-label="Remove shipping option"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  </div>
                                  {isExpanded && (
                                    <div
                                      id={detailsId}
                                      className="px-5 pb-5 pl-[4.75rem]"
                                    >
                                      <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 lg:grid-cols-[minmax(0,1fr)_180px]">
                                        <div>
                                          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                            Method details
                                          </p>
                                          <p className="mt-1 text-xs text-gray-500">
                                            {
                                              'Used to quote and create a return label.'
                                            }
                                          </p>
                                          <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-gray-700">
                                            Customer label
                                          </label>
                                          <input
                                            type="text"
                                            value={option.displayName}
                                            maxLength={
                                              SHIPPING_OPTION_LABEL_MAX_LENGTH
                                            }
                                            onChange={(event) =>
                                              handleUpdateShippingOption(
                                                option.id,
                                                {
                                                  displayName:
                                                    event.target.value,
                                                },
                                              )
                                            }
                                            className="mt-1 w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                          />
                                        </div>
                                        <div>
                                          <p className="text-xs font-bold uppercase tracking-wide text-gray-700">
                                            Available to customers
                                          </p>
                                          <div className="mt-2 flex items-center gap-2">
                                            <Toggle
                                              isEnabled={isAvailable}
                                              onChange={(isAvailable) =>
                                                handleUpdateShippingOption(
                                                  option.id,
                                                  {
                                                    enabled: isAvailable,
                                                    customerVisible:
                                                      isAvailable,
                                                  },
                                                )
                                              }
                                              ariaLabel={`Make ${getConfiguredShippingMethodName(
                                                option,
                                              )} available to customers`}
                                            />
                                            <span className="text-xs text-gray-600">
                                              {isAvailable
                                                ? 'Available'
                                                : 'Unavailable'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
                        <div className="max-w-xl">
                          <h4 className="text-sm font-bold text-gray-900">
                            Label output
                          </h4>
                          <p className="mt-1 text-sm text-gray-500">
                            Choose the label format and size.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label
                              htmlFor="return-label-format"
                              className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1"
                            >
                              Label format
                            </label>
                            <select
                              id="return-label-format"
                              value={labelFormat}
                              onChange={(event) =>
                                setLabelFormat(event.target.value)
                              }
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                            >
                              <option value="PDF">PDF</option>
                              <option value="PNG">PNG</option>
                              <option value="ZPL">ZPL</option>
                            </select>
                          </div>
                          <div>
                            <label
                              htmlFor="return-label-page-size"
                              className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1"
                            >
                              Label page size
                            </label>
                            <select
                              id="return-label-page-size"
                              value={labelPageSize}
                              onChange={(event) =>
                                setLabelPageSize(event.target.value)
                              }
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                            >
                              <option value="4x6">4x6</option>
                              <option value="A4">A4</option>
                              <option value="LETTER">Letter</option>
                            </select>
                          </div>
                        </div>

                        <div className="border-t border-gray-100 pt-5">
                          <div className="mb-3">
                            <h5 className="text-sm font-bold text-gray-900">
                              Default package
                            </h5>
                            <p className="mt-1 text-sm text-gray-500">
                              {
                                'Used when item weight or dimensions are missing.'
                              }
                            </p>
                          </div>
                          <p className="mb-3 text-sm text-gray-500">
                            Leave blank to use standard defaults.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                            <div>
                              <label
                                htmlFor="return-default-weight-oz"
                                className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1"
                              >
                                Weight
                              </label>
                              <div className="relative">
                                <NonNegativeNumberInput
                                  id="return-default-weight-oz"
                                  min="0"
                                  step="0.1"
                                  placeholder="16"
                                  value={defaultWeightOz}
                                  onChange={(event) =>
                                    setDefaultWeightOz(event.target.value)
                                  }
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm pr-12"
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                                  oz
                                </span>
                              </div>
                            </div>
                            <div>
                              <label
                                htmlFor="return-default-length-in"
                                className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1"
                              >
                                Length
                              </label>
                              <div className="relative">
                                <NonNegativeNumberInput
                                  id="return-default-length-in"
                                  min="0"
                                  step="0.1"
                                  placeholder="12"
                                  value={defaultLengthIn}
                                  onChange={(event) =>
                                    setDefaultLengthIn(event.target.value)
                                  }
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm pr-10"
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                                  in
                                </span>
                              </div>
                            </div>
                            <div>
                              <label
                                htmlFor="return-default-width-in"
                                className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1"
                              >
                                Width
                              </label>
                              <div className="relative">
                                <NonNegativeNumberInput
                                  id="return-default-width-in"
                                  min="0"
                                  step="0.1"
                                  placeholder="9"
                                  value={defaultWidthIn}
                                  onChange={(event) =>
                                    setDefaultWidthIn(event.target.value)
                                  }
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm pr-10"
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                                  in
                                </span>
                              </div>
                            </div>
                            <div>
                              <label
                                htmlFor="return-default-height-in"
                                className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1"
                              >
                                Height
                              </label>
                              <div className="relative">
                                <NonNegativeNumberInput
                                  id="return-default-height-in"
                                  min="0"
                                  step="0.1"
                                  placeholder="2"
                                  value={defaultHeightIn}
                                  onChange={(event) =>
                                    setDefaultHeightIn(event.target.value)
                                  }
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm pr-10"
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                                  in
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Return Locations (Multiple) */}
                    <div className="border-t border-gray-100 pt-6">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900">
                              Return locations
                            </h3>
                            <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded border border-gray-200 uppercase font-medium">
                              Multiple
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500 max-w-2xl">
                            {
                              'Sync Shopify warehouses or add locations. Saved locations change only when you sync again.'
                            }
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            size={SizeEnum.sm}
                            variant="outlined"
                            onClick={handleSyncShopifyLocations}
                            isLoading={shopifyReturnLocationsLoading}
                            disabled={!storeId}
                          >
                            <RefreshIcon className="w-4 h-4 mr-1" />
                            Sync from Shopify
                          </Button>
                          <Button
                            size={SizeEnum.sm}
                            variant="outlined"
                            onClick={handleAddLocation}
                          >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            Add location
                          </Button>
                        </div>
                      </div>
                      {shopifyReturnLocationsError && (
                        <p className="mb-3 text-xs text-red-600">
                          Could not load Shopify warehouses. Add a location
                          manually.
                        </p>
                      )}

                      <div className="space-y-3">
                        {returnLocations.length === 0 && (
                          <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                            <p className="text-sm text-gray-500">
                              No return locations.
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              Sync Shopify or add an address.
                            </p>
                            <button
                              onClick={handleAddLocation}
                              className="text-sm font-medium text-blue-600 hover:text-blue-700 mt-1"
                            >
                              Add manually
                            </button>
                          </div>
                        )}

                        {returnLocations.map((loc) => (
                          <div
                            key={loc.id}
                            className={classNames(
                              'p-3 rounded-lg border flex items-start justify-between group',
                              loc.isDefault
                                ? 'bg-white border-blue-200 shadow-sm'
                                : 'bg-white border-gray-200',
                            )}
                          >
                            <div>
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-bold text-gray-900">
                                  {loc.label || 'Warehouse'}
                                </span>
                                {loc.isDefault && (
                                  <span className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide">
                                    Default
                                  </span>
                                )}
                                {loc.source === 'SHOPIFY' && (
                                  <span className="bg-green-50 text-green-700 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide">
                                    Shopify
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {loc.street}
                                {loc.street2 ? (
                                  <>
                                    <br />
                                    {loc.street2}
                                    <br />
                                  </>
                                ) : ', '}
                                {loc.city}, {loc.state} {loc.zip}
                                {loc.country ? `, ${loc.country}` : ''}
                              </p>
                              {(loc.email || loc.phone) && (
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                  {loc.email && (
                                    <span className="flex items-center gap-1">
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                      </svg>
                                      {loc.email}
                                    </span>
                                  )}
                                  {loc.phone && (
                                    <span className="flex items-center gap-1">
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                      </svg>
                                      {loc.phone}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleEditLocation(loc)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                                title="Edit Location"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteLocation(loc.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                                title="Delete Location"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Modal for Add/Edit Location (Inline implementation for simplicity within this file) */}
                      {isAddLocationModalOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
                            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                              <h3 className="text-lg font-bold text-gray-900">
                                {editingLocationId
                                  ? 'Edit return location'
                                  : 'Add return location'}
                              </h3>
                              <button
                                type="button"
                                onClick={requestCloseLocationForm}
                                aria-label="Close return location editor"
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <span className="text-2xl">&times;</span>
                              </button>
                            </div>

                            <div className="p-6 space-y-4">
                              <div>
                                <label
                                  htmlFor="return-location-label"
                                  className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                >
                                  Location name
                                </label>
                                <input
                                  id="return-location-label"
                                  type="text"
                                  value={locationForm.label}
                                  onChange={(e) =>
                                    updateLocationFormField(
                                      'label',
                                      e.target.value,
                                    )
                                  }
                                  maxLength={RETURN_LOCATION_FIELD_LIMITS.label}
                                  aria-invalid={Boolean(
                                    locationFormErrors.label,
                                  )}
                                  placeholder="West Coast Warehouse"
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                />
                                {locationFormErrors.label && (
                                  <p
                                    role="alert"
                                    className="mt-1 text-xs text-red-600"
                                  >
                                    {locationFormErrors.label}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label
                                  htmlFor="return-location-street"
                                  className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                >
                                  Return-to street address
                                </label>
                                <input
                                  id="return-location-street"
                                  type="text"
                                  value={locationForm.street}
                                  onChange={(e) =>
                                    updateLocationFormField(
                                      'street',
                                      e.target.value,
                                    )
                                  }
                                  maxLength={
                                    RETURN_LOCATION_FIELD_LIMITS.street
                                  }
                                  aria-invalid={Boolean(
                                    locationFormErrors.street,
                                  )}
                                  placeholder="123 Warehouse St"
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                />
                                {locationFormErrors.street && (
                                  <p
                                    role="alert"
                                    className="mt-1 text-xs text-red-600"
                                  >
                                    {locationFormErrors.street}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label
                                  htmlFor="return-location-street2"
                                  className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                >
                                  Address line 2 (optional)
                                </label>
                                <input
                                  id="return-location-street2"
                                  type="text"
                                  value={locationForm.street2 || ''}
                                  onChange={(e) =>
                                    updateLocationFormField('street2', e.target.value)
                                  }
                                  maxLength={RETURN_LOCATION_FIELD_LIMITS.street2}
                                  aria-invalid={Boolean(locationFormErrors.street2)}
                                  placeholder="Unit, suite or building"
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                />
                                {locationFormErrors.street2 && (
                                  <p role="alert" className="mt-1 text-xs text-red-600">
                                    {locationFormErrors.street2}
                                  </p>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label
                                    htmlFor="return-location-email"
                                    className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                  >
                                    Contact Email
                                  </label>
                                  <input
                                    id="return-location-email"
                                    type="email"
                                    value={locationForm.email}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'email',
                                        e.target.value,
                                      )
                                    }
                                    maxLength={
                                      RETURN_LOCATION_FIELD_LIMITS.email
                                    }
                                    aria-invalid={Boolean(
                                      locationFormErrors.email,
                                    )}
                                    placeholder="warehouse@example.com"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                  />
                                  {locationFormErrors.email && (
                                    <p
                                      role="alert"
                                      className="mt-1 text-xs text-red-600"
                                    >
                                      {locationFormErrors.email}
                                    </p>
                                  )}
                                </div>
                                <div>
                                  <label
                                    htmlFor="return-location-phone"
                                    className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                  >
                                    Contact Phone
                                  </label>
                                  <input
                                    id="return-location-phone"
                                    type="tel"
                                    inputMode="tel"
                                    value={locationForm.phone}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'phone',
                                        sanitizePhoneInput(e.target.value),
                                      )
                                    }
                                    maxLength={
                                      RETURN_LOCATION_FIELD_LIMITS.phone
                                    }
                                    aria-invalid={Boolean(
                                      locationFormErrors.phone,
                                    )}
                                    placeholder="(000) 000-0000"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                  />
                                  {locationFormErrors.phone && (
                                    <p
                                      role="alert"
                                      className="mt-1 text-xs text-red-600"
                                    >
                                      {locationFormErrors.phone}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                  <label
                                    htmlFor="return-location-country"
                                    className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                  >
                                    Country
                                  </label>
                                  <input
                                    id="return-location-country"
                                    type="text"
                                    value={locationForm.country}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'country',
                                        e.target.value,
                                      )
                                    }
                                    maxLength={
                                      RETURN_LOCATION_FIELD_LIMITS.country
                                    }
                                    aria-invalid={Boolean(
                                      locationFormErrors.country,
                                    )}
                                    placeholder="United States"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                  />
                                  {locationFormErrors.country && (
                                    <p
                                      role="alert"
                                      className="mt-1 text-xs text-red-600"
                                    >
                                      {locationFormErrors.country}
                                    </p>
                                  )}
                                </div>
                                <div className="col-span-1">
                                  <label
                                    htmlFor="return-location-country-code"
                                    className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                  >
                                    Country code
                                  </label>
                                  <input
                                    id="return-location-country-code"
                                    type="text"
                                    maxLength={
                                      RETURN_LOCATION_FIELD_LIMITS.country_alpha2
                                    }
                                    value={locationForm.country_alpha2}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'country_alpha2',
                                        sanitizeCountryCodeInput(
                                          e.target.value,
                                        ),
                                      )
                                    }
                                    aria-invalid={Boolean(
                                      locationFormErrors.country_alpha2,
                                    )}
                                    placeholder="US"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm uppercase"
                                  />
                                  {locationFormErrors.country_alpha2 ? (
                                    <p
                                      role="alert"
                                      className="mt-1 text-xs text-red-600"
                                    >
                                      {locationFormErrors.country_alpha2}
                                    </p>
                                  ) : (
                                    <p className="mt-1 text-[11px] leading-4 text-gray-500">
                                      2-letter ISO code used for labels.
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-1">
                                  <label
                                    htmlFor="return-location-city"
                                    className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                  >
                                    City
                                  </label>
                                  <input
                                    id="return-location-city"
                                    type="text"
                                    value={locationForm.city}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'city',
                                        e.target.value,
                                      )
                                    }
                                    maxLength={
                                      RETURN_LOCATION_FIELD_LIMITS.city
                                    }
                                    aria-invalid={Boolean(
                                      locationFormErrors.city,
                                    )}
                                    placeholder="City"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                  />
                                  {locationFormErrors.city && (
                                    <p
                                      role="alert"
                                      className="mt-1 text-xs text-red-600"
                                    >
                                      {locationFormErrors.city}
                                    </p>
                                  )}
                                </div>
                                <div className="col-span-1">
                                  <label
                                    htmlFor="return-location-state"
                                    className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                  >
                                    State
                                  </label>
                                  <input
                                    id="return-location-state"
                                    type="text"
                                    value={locationForm.state}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'state',
                                        e.target.value,
                                      )
                                    }
                                    maxLength={
                                      RETURN_LOCATION_FIELD_LIMITS.state
                                    }
                                    aria-invalid={Boolean(
                                      locationFormErrors.state,
                                    )}
                                    placeholder="State"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                  />
                                  {locationFormErrors.state && (
                                    <p
                                      role="alert"
                                      className="mt-1 text-xs text-red-600"
                                    >
                                      {locationFormErrors.state}
                                    </p>
                                  )}
                                </div>
                                <div className="col-span-1">
                                  <label
                                    htmlFor="return-location-zip"
                                    className="block text-xs font-bold text-gray-700 uppercase mb-1"
                                  >
                                    Zip
                                  </label>
                                  <input
                                    id="return-location-zip"
                                    type="text"
                                    value={locationForm.zip}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'zip',
                                        e.target.value,
                                      )
                                    }
                                    maxLength={RETURN_LOCATION_FIELD_LIMITS.zip}
                                    aria-invalid={Boolean(
                                      locationFormErrors.zip,
                                    )}
                                    placeholder="Zip"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                  />
                                  {locationFormErrors.zip && (
                                    <p
                                      role="alert"
                                      className="mt-1 text-xs text-red-600"
                                    >
                                      {locationFormErrors.zip}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="pt-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    id="isDefault"
                                    checked={locationForm.isDefault}
                                    onChange={(e) =>
                                      updateLocationFormField(
                                        'isDefault',
                                        e.target.checked,
                                      )
                                    }
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <label
                                    htmlFor="isDefault"
                                    className="text-sm text-gray-900 font-medium select-none cursor-pointer"
                                  >
                                    Set as primary return location
                                  </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-1 ml-6">
                                  Primary location is the default return-to
                                  address for generated labels.
                                </p>
                              </div>
                            </div>

                            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t border-gray-100">
                              <Button
                                size={SizeEnum.sm}
                                variant="outlined"
                                onClick={requestCloseLocationForm}
                              >
                                Cancel
                              </Button>
                              <Button size={SizeEnum.sm} onClick={saveLocation}>
                                {editingLocationId
                                  ? 'Update location'
                                  : 'Add location'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </div>

              <div className="2xl:col-span-4 space-y-6">
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-gray-50 text-gray-500">
                        <TruckIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          Shipping summary
                        </h3>
                      </div>
                    </div>
                    <span
                      className={classNames(
                        'rounded px-2 py-0.5 text-[10px] font-bold uppercase',
                        visibleShippingOptionsCount
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600',
                      )}
                    >
                      {visibleShippingOptionsCount ? 'Live' : 'Setup needed'}
                    </span>
                  </div>
                  <div className="space-y-3 p-5">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                        Customer methods
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {orderedShippingOptions.length} configured ·{' '}
                        {visibleShippingOptionsCount} available
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                        Label output
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {labelOutputSummary}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-gray-500">
                        Default package covers missing item data.
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                        Return location
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {primaryReturnLocation?.label || 'No location saved'}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-gray-500">
                        {returnLocations.length
                          ? `${returnLocations.length} location${
                              returnLocations.length === 1 ? '' : 's'
                            } configured.`
                          : 'Add a location before generating labels.'}
                      </p>
                    </div>
                  </div>
                </section>

                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-gray-50 text-gray-500">
                      <ShieldCheckIcon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        How labels work
                      </h3>
                      <p className="mt-1 text-sm leading-5 text-gray-500">
                        Labels use the selected method, format, and return
                        location.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3 p-5">
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                      <p className="text-sm font-semibold text-blue-900">
                        SHIPAID rates
                      </p>
                      <p className="mt-1 text-xs leading-5 text-blue-800">
                        Available labels are confirmed before submission.
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">
                        QR code drop-off
                      </p>
                      <p className="mt-1 text-xs leading-5 text-gray-500">
                        {
                          'Supported carriers can offer QR drop-off and printable labels.'
                        }
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </SettingsViewWrapper>
    );
  },
);

export default CarrierSettings;
